import { MenuOpenOutlined } from "@mui/icons-material"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"

import Button from "@components/button"
import CustomerSelect from "@components/customer_select"
import LeftDrawer from "@components/persist_drawer"
import SectionBar from "@components/section_bar"
import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import {
  CallLogsFormData,
  useCallLogsPageContext
} from "@contexts/main/call_logs_page_context"
import { deleteCallAdminLogsMutation } from "@domain/call_logs"
import { Box, InputLabel, Stack } from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import dayjs from "dayjs"
import "dayjs/locale/tr"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"

export default function CallAdminLogsPageLeftDrawer() {
  const {
    isLeftDrawerOpen,
    toggleIsLeftDrawerOpen,
    translate,
    setFilters,
    filters
  } = useCallLogsPageContext()

  const { control, handleSubmit, setValue, watch, reset } =
    useForm<CallLogsFormData>({
      defaultValues: filters
    })

  useEffect(() => {
    reset(filters)
  }, [filters, reset])

  const watchedStartDate = watch("startDate")
  const watchedEndDate = watch("endDate")

  const [isStartDateOpen, setIsStartDateOpen] = useState(false)
  const [isEndDateOpen, setIsEndDateOpen] = useState(false)
  const [isDateFocus, setIsDateFocus] = useState(false)

  const onSubmit = (data: CallLogsFormData) => {
    let { startDate, endDate } = data
    if (startDate && endDate && startDate > endDate) {
      ;[startDate, endDate] = [endDate, startDate]
    }
    setFilters({
      ...data,
      startDate: startDate
        ? dayjs(startDate).startOf("day").toDate()
        : undefined,
      endDate: endDate ? dayjs(endDate).endOf("day").toDate() : undefined,
      filterTrigger: Date.now()
    })
  }

  const onDelete = async (data: CallLogsFormData) => {
    if (!data.customerId) {
      showErrorToast(translate("customer_required") || "Customer Required")
      return
    }

    const confirm = window.confirm(
      translate("delete_call_logs_confirm") ||
        "Are you sure you want to delete call logs for the selected criteria?"
    )
    if (!confirm) return

    const response = await deleteCallAdminLogsMutation({
      customerId: data.customerId,
      startDate: data.startDate?.getTime(),
      endDate: data.endDate?.getTime()
    })

    if (response.isSuccess) {
      showSuccessToast(
        translate("delete_success") || "Logs deleted successfully"
      )
      setFilters((prev) => ({ ...prev, filterTrigger: Date.now() }))
    } else {
      showErrorToast(response.errorMessage || "Delete failed")
    }
  }

  return (
    <LeftDrawer
      isOpen={!!isLeftDrawerOpen}
      header={<Header setClose={toggleIsLeftDrawerOpen} />}
      sx={{
        borderRight: 1,
        borderColor: "divider",
        width: "16rem",
        minWidth: "16rem",
        maxWidth: "16rem"
      }}
    >
      <Stack gap={2} p={2}>
        <CustomerSelect
          control={control}
          name="customerId"
          label={translate("customer")}
        />

        <Stack>
          <InputLabel
            sx={{
              color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
              alignItems: "center"
            }}
          >
            <Typography>{translate("start_date")}</Typography>
          </InputLabel>
          <Box
            sx={{
              border: "1px solid #DDDDDD",
              borderRadius: 1,
              ":hover": {
                border: "1px solid #000000"
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
              <DatePicker
                format="DD.MM.YYYY"
                sx={{
                  "& input": { width: "100%" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none"
                    }
                  },
                  width: "100%"
                }}
                value={watchedStartDate ? dayjs(watchedStartDate) : null}
                disableOpenPicker
                open={isStartDateOpen}
                onClose={() => setIsStartDateOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setIsStartDateOpen(true),
                    onFocus: () => setIsDateFocus(true),
                    onBlur: () => setIsDateFocus(false)
                  },
                  field: {
                    clearable: true,
                    onClear: () => setValue("startDate", undefined)
                  }
                }}
                onChange={(date) => {
                  setValue(
                    "startDate",
                    date ? date.startOf("day").toDate() : undefined
                  )
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>

        <Stack>
          <InputLabel
            sx={{
              color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
              alignItems: "center"
            }}
          >
            <Typography>{translate("end_date")}</Typography>
          </InputLabel>
          <Box
            sx={{
              border: "1px solid #DDDDDD",
              borderRadius: 1,
              ":hover": {
                border: "1px solid #000000"
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
              <DatePicker
                format="DD.MM.YYYY"
                sx={{
                  "& input": { width: "100%" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none"
                    }
                  },
                  width: "100%"
                }}
                value={watchedEndDate ? dayjs(watchedEndDate) : null}
                disableOpenPicker
                open={isEndDateOpen}
                onClose={() => setIsEndDateOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setIsEndDateOpen(true),
                    onFocus: () => setIsDateFocus(true),
                    onBlur: () => setIsDateFocus(false)
                  },
                  field: {
                    clearable: true,
                    onClear: () => setValue("endDate", undefined)
                  }
                }}
                onChange={(date) => {
                  setValue(
                    "endDate",
                    date ? date.endOf("day").toDate() : undefined
                  )
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>

        <Button onClick={handleSubmit(onSubmit)}>{translate("filter")}</Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleSubmit(onDelete)}
        >
          {translate("delete_logs") || "Delete Logs"}
        </Button>
      </Stack>
    </LeftDrawer>
  )
}

function Header({ setClose }: { setClose?: () => void }) {
  const { translate } = useCallLogsPageContext()

  return (
    <SectionBar
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        p: 1
      }}
    >
      <Typography p={1} variant="h6">
        {translate("call_logs")}
      </Typography>
      <IconButton
        disableRipple
        sx={{
          ":hover": {
            color: PRIMARY
          }
        }}
        onClick={setClose}
      >
        <MenuOpenOutlined />
      </IconButton>
    </SectionBar>
  )
}
